// Reusable Support Request form.
//   Used by:
//     - /app/support (in-app, JWT)
//     - /support (public marketing site, anonymous)

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2, CheckCircle2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import logger from '@/lib/logger';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ISSUE_TYPES = [
  { value: 'Bug Report', label: 'Bug Report', helper: "Something is broken or behaving unexpectedly." },
  { value: 'Feature Request', label: 'Feature Request', helper: "An idea for a change or new capability." },
  { value: 'General Support', label: 'General Support', helper: "A question or guidance on how to use the platform." },
  { value: 'Account Help', label: 'Account Help', helper: "Login issues, billing questions, user permissions." },
];

export default function SupportRequestForm({ mode = 'in_app', token, prefill = {} }) {
  // mode: 'in_app' (logged-in) | 'public' (marketing site)
  const [issueType, setIssueType] = useState(prefill.issueType || 'General Support');
  const [description, setDescription] = useState('');
  const [featureIdea, setFeatureIdea] = useState('');
  const [currentProcess, setCurrentProcess] = useState('');
  const [featureImpact, setFeatureImpact] = useState('');
  const [relatedUrl, setRelatedUrl] = useState('');
  const [email, setEmail] = useState(prefill.email || '');
  const [name, setName] = useState(prefill.name || '');
  const [companyName, setCompanyName] = useState(prefill.companyName || '');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const isFeatureRequest = issueType === 'Feature Request';
  const helper = ISSUE_TYPES.find(t => t.value === issueType)?.helper;

  const reset = () => {
    setIssueType('General Support');
    setDescription('');
    setFeatureIdea(''); setCurrentProcess(''); setFeatureImpact('');
    setRelatedUrl('');
    if (mode === 'public') { setEmail(''); setName(''); setCompanyName(''); }
    setResult(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!description.trim() || description.trim().length < 10) {
      toast.error('Please describe the issue (at least 10 characters).');
      return;
    }
    if (mode === 'public' && (!email || !name)) {
      toast.error('Email and name are required.');
      return;
    }
    setSubmitting(true);
    const body = {
      issueType,
      description: description.trim(),
      featureIdea: isFeatureRequest ? featureIdea.trim() || null : null,
      currentProcess: isFeatureRequest ? currentProcess.trim() || null : null,
      featureImpact: isFeatureRequest ? featureImpact.trim() || null : null,
      relatedUrl: relatedUrl.trim() || null,
      companyName: companyName.trim() || null,
    };
    if (mode === 'public') { body.email = email.trim(); body.name = name.trim(); }

    const endpoint = mode === 'public' ? '/public/support-request' : '/support-requests';
    const headers = { 'Content-Type': 'application/json' };
    if (mode === 'in_app' && token) headers.Authorization = `Bearer ${token}`;

    try {
      const res = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const msg = Array.isArray(err.detail) ? err.detail[0]?.msg : (err.detail || 'Submission failed');
        toast.error(typeof msg === 'string' ? msg : 'Submission failed');
        setSubmitting(false);
        return;
      }
      const data = await res.json();
      setResult(data);
      toast.success(`Ticket ${data.ticketNumber} submitted`);
    } catch (e) {
      logger.warn('SupportRequestForm: submit failed', e);
      toast.error('Network error. Please try again.');
    }
    setSubmitting(false);
  };

  if (result) {
    return (
      <div className="bg-white border border-[#E6E0D9] rounded-lg p-8 text-center" data-testid="support-form-success">
        <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-6 h-6 text-emerald-700" />
        </div>
        <h3 className="text-xl font-light text-[#2C2420] mb-1">Thanks — your request is in.</h3>
        <p className="text-sm text-[#5D524D] mb-2">
          Reference: <strong className="font-mono">{result.ticketNumber}</strong>
        </p>
        <p className="text-xs text-[#8C8078] mb-6 max-w-md mx-auto">
          {issueType === 'Bug Report'
            ? "We'll take a look and respond by email. Bug reports are prioritised first."
            : "We'll review and get back to you. Feature requests are batched against the public roadmap."}
        </p>
        <Button variant="outline" onClick={reset} data-testid="support-form-new-request">
          Submit another <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5 bg-white border border-[#E6E0D9] rounded-lg p-6" data-testid="support-form">
      <div>
        <Label className="mb-1 block">Please choose your issue below</Label>
        <Select value={issueType} onValueChange={setIssueType}>
          <SelectTrigger data-testid="support-form-issue-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            {ISSUE_TYPES.map(t => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {helper && <p className="text-xs text-[#8C8078] mt-1.5">{helper}</p>}
      </div>

      {mode === 'public' && (
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="sup-name" className="mb-1 block">Your name *</Label>
            <Input id="sup-name" value={name} onChange={e => setName(e.target.value)} placeholder="Jane Smith" data-testid="support-form-name" />
          </div>
          <div>
            <Label htmlFor="sup-email" className="mb-1 block">Your email *</Label>
            <Input id="sup-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@firm.com" data-testid="support-form-email" />
          </div>
        </div>
      )}

      <div>
        <Label htmlFor="sup-description" className="mb-1 block">Description *</Label>
        <Textarea
          id="sup-description"
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Please provide a detailed description of your issue. We'll get back to you shortly."
          className="min-h-[140px]"
          data-testid="support-form-description"
        />
      </div>

      {isFeatureRequest && (
        <div className="space-y-4 border-l-2 border-[#A86A3A]/40 pl-4">
          <div>
            <Label htmlFor="sup-idea" className="mb-1 block">Feature idea</Label>
            <Textarea id="sup-idea" value={featureIdea} onChange={e => setFeatureIdea(e.target.value)} placeholder="Describe the change you'd like to see." data-testid="support-form-idea" />
          </div>
          <div>
            <Label htmlFor="sup-process" className="mb-1 block">Current process / pain points</Label>
            <Textarea id="sup-process" value={currentProcess} onChange={e => setCurrentProcess(e.target.value)} placeholder="How do you handle this today, and what hurts about it?" data-testid="support-form-process" />
          </div>
          <div>
            <Label htmlFor="sup-impact" className="mb-1 block">Feature impact</Label>
            <Textarea id="sup-impact" value={featureImpact} onChange={e => setFeatureImpact(e.target.value)} placeholder="What does this unlock — time saving, accuracy, new revenue, etc?" data-testid="support-form-impact" />
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="sup-company" className="mb-1 block">Company / firm name</Label>
          <Input id="sup-company" value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="Optional" data-testid="support-form-company" />
        </div>
        <div>
          <Label htmlFor="sup-url" className="mb-1 block">Related URL</Label>
          <Input id="sup-url" value={relatedUrl} onChange={e => setRelatedUrl(e.target.value)} placeholder="Where the issue happened" data-testid="support-form-url" />
        </div>
      </div>

      <Button type="submit" disabled={submitting} className="w-full" data-testid="support-form-submit">
        {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
        Submit request
      </Button>
    </form>
  );
}
